import React, { useRef, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import { Switch } from "@rneui/themed";
import { File, Remove } from "../../../svgs/CustomCoursework";
import FormLayout from "../common/FormLayout";
import { DatePickerInput } from "react-native-paper-dates";
import { StoragePathType } from "../../../../graphql/generated/graphql";
import useFileUpload from "../../../../hooks/useFileUpload";

const AssignmentForm = ({ activityData, onSave, onCancel, error, isSubmitting, isNewActivity = false }) => {
  const maybeFormatDueDate = (date) => {
    if (!date) return "";
    if (typeof date === "string" && date.endsWith("Z")) {
      return new Date(date);
    }
    return date;
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      videoUrl: activityData?.videoUrl || "",
      files: activityData?.files || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate) || "",
      enableFileUpload: activityData?.enableFileUpload ?? true,
    },
  });

  const editorRef = useRef(null);
  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchFiles = watch("files");
  const watchDueDate = watch("dueDate");

  // Initialize the editor with the description content
  useEffect(() => {
    if (editorRef.current && activityData?.description) {
      // Set the initial content once when the component mounts
      editorRef.current.setContentHTML(activityData.description);
    }
  }, [activityData]);

  // File upload handling
  const handleAfterUpload = async ({ id, fileName, fileURL, mimeType, size }) => {
    const newFiles = [
      ...watchFiles,
      {
        fileId: id,
        fileName: fileName,
        fileUrl: fileURL,
        contentType: mimeType,
        size: size,
      },
    ];
    setValue("files", newFiles);
  };

  const { renderFileUpload } = useFileUpload({
    storagePath: StoragePathType.ResourceDocument,
    afterUpload: handleAfterUpload,
  });

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Helper function to truncate long text
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Form submission
  const onSubmit = (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      videoUrl: data.videoUrl || "",
      files: data.files || [],
      dueDate: data.dueDate,
      enableFileUpload: data.enableFileUpload,
      type: "Assignment",
    };

    onSave(activityData);
  };

  return (
    <FormLayout
      title={`${isNewActivity ? "Create" : "Edit"} Assignment`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Create" : "Save"}
      disableSubmit={!watchActivityName}
      error={error}>
      <CustomText text="Assignment Name" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput placeholder="Enter assignment name" onBlur={onBlur} onChangeText={onChange} value={value} />
        )}
      />
      {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <DatePickerInput
            locale="en"
            value={value}
            onChange={onChange}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            selectionColor={false}
            activeOutlineColor={"blue"}
            withDateFormatInLabel={false}
            placeholder="MM/DD/YYYY"
            inputProps={{
              color: "#000000",
              mode: "outline",
            }}
            theme={{
              colors: "#000000",
              roundness: 8,
              opacity: 1,
            }}
            style={styles.datePicker}
            aria-label="Due date date picker"
          />
        )}
      />
      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Activity Description" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <>
            <RichEditor
              style={styles.editor}
              ref={editorRef}
              initialContentHTML={value}
              onChange={(content) => {
                // Directly set the form value when the editor content changes
                onChange(content);
              }}
              placeholder="Enter assignment description"
            />
            <RichToolbar
              editor={editorRef}
              actions={[actions.setBold, actions.setItalic, actions.setUnderline, actions.heading1]}
              style={styles.toolbar}
            />
          </>
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="File Upload" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload files students will need for this assignment" />
      <CustomSpacing type="vertical" size="s" />
      {renderFileUpload()}
      {renderFileList()}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Video URL" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Paste either a YouTube or Vimeo link here" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="videoUrl"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Paste either a YouTube or Vimeo link here"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.toggleContainer}>
        <CustomText text="Enable student file upload" size="m" weight="ultraBold" />
        <CustomSpacing type="horizontal" size="s" />
        <Controller
          name="enableFileUpload"
          control={control}
          render={({ field: { onChange, value } }) => <Switch value={value} onValueChange={onChange} />}
        />
      </View>
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  editor: {
    minHeight: 200,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 4,
  },
  toolbar: {
    backgroundColor: "#f1f1f1",
    borderColor: "#ccc",
    borderTopWidth: 1,
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  toggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
  datePicker: {
    borderWidth: 0,
    paddingVertical: 10,
    backgroundColor: "#F8F8F8",
    borderColor: "#000000",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
});

export default AssignmentForm;
